import { useState, useRef } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { Plus, Trash2, Grid2x2, Grid3x3, Rows3, X } from 'lucide-react';
import { toast } from 'sonner';

const GalleryBlockComponent = ({ node, updateAttributes, selected, deleteNode }) => {
  const { images, columns, gap } = node.attrs;
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAddImages = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const newUrls = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/dropbox/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (data.url) newUrls.push(data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newUrls.length) {
      updateAttributes({ images: [...images, ...newUrls] });
      toast.success(`${newUrls.length} image(s) added to gallery`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    const updated = images.filter((_, i) => i !== index);
    updateAttributes({ images: updated });
  };

  return (
    <NodeViewWrapper
      className={`my-6 ${selected ? 'ring-2 ring-blue-400 ring-offset-2 rounded p-2' : ''}`}
      data-testid="editor-gallery"
    >
      {selected && (
        <div className="flex items-center gap-2 mb-3" contentEditable={false}>
          <span className="text-xs text-stone-500 uppercase tracking-wide font-medium">Gallery</span>
          <div className="flex items-center gap-1 ml-auto bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => updateAttributes({ columns: 2 })}
              className={`p-1.5 rounded ${columns === 2 ? 'bg-white shadow text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
              title="2 columns"
            >
              <Grid2x2 size={14} />
            </button>
            <button
              onClick={() => updateAttributes({ columns: 3 })}
              className={`p-1.5 rounded ${columns === 3 ? 'bg-white shadow text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
              title="3 columns"
            >
              <Grid3x3 size={14} />
            </button>
            <button
              onClick={() => updateAttributes({ columns: 4 })}
              className={`p-1.5 rounded ${columns === 4 ? 'bg-white shadow text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
              title="4 columns"
            >
              <Rows3 size={14} />
            </button>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1 px-3 py-1.5 bg-stone-900 text-white rounded text-xs hover:bg-stone-700 disabled:opacity-50"
          >
            <Plus size={12} /> {uploading ? 'Uploading...' : 'Add Images'}
          </button>
          <button
            onClick={deleteNode}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
            title="Delete gallery"
          >
            <Trash2 size={14} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleAddImages}
            className="hidden"
          />
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `${gap}px`,
        }}
      >
        {images.map((img, i) => (
          <div key={i} className="relative group aspect-square overflow-hidden rounded bg-stone-100">
            <img
              src={img}
              alt={`Gallery ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {selected && (
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                contentEditable={false}
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}

        {images.length === 0 && (
          <div
            className="col-span-full flex flex-col items-center justify-center py-12 border-2 border-dashed border-stone-300 rounded-lg text-stone-400 cursor-pointer hover:border-stone-400 hover:text-stone-500 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus size={24} className="mb-2" />
            <span className="text-sm">Click to add images to gallery</span>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export default GalleryBlockComponent;
